import API_CONFIG from "../config/api";
import { auth } from "../firebase/config";
import { track } from "./analytics";

// Collapse dynamic path segments (ids) into ":id" so api_error stays
// low-cardinality (e.g. /games/abc123/join -> /games/:id/join).
const normalizeEndpoint = (endpoint) =>
  endpoint
    .split("?")[0]
    .replace(/\/[0-9a-fA-F-]{8,}(?=\/|$)/g, "/:id")
    .replace(/\/\d+(?=\/|$)/g, "/:id");

class ApiService {
  setOnTokenRefresh(callback) {
    this.onTokenRefresh = callback;
  }

  setNavigationHandler(handler) {
    this.navigationHandler = handler;
  }

  constructor() {
    this.baseURL = API_CONFIG.BASE_URL;
    this.timeout = API_CONFIG.TIMEOUT;
    this.navigationHandler = null;
  }

  // Helper method for making HTTP requests
  async makeRequest(endpoint, options = {}, retry = true) {
    const url = `${this.baseURL}/api${endpoint}`;
    const token = JSON.parse(localStorage.getItem("accessToken"));

    const headers = {
      ...(options.skipContentType
        ? {}
        : { "Content-Type": "application/json" }),
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    };

    const defaultOptions = {
      method: "GET",
      headers,
      ...options,
    };

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    let response = await fetch(url, {
      ...defaultOptions,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    // If 401, try to refresh token and repeat request once
    if (response.status === 401 && retry && auth.currentUser) {
      try {
        const newIdToken = await auth.currentUser.getIdToken(true);
        localStorage.setItem("accessToken", JSON.stringify(newIdToken));
        // Update Authorization header
        defaultOptions.headers["Authorization"] = `Bearer ${newIdToken}`;
        // Repeat request with new token, but don't retry again if fails
        response = await fetch(url, {
          ...defaultOptions,
          signal: controller.signal,
        });
        // Dispatch token refresh event
        window.dispatchEvent(
          new CustomEvent("tokenRefreshed", {
            detail: { token: newIdToken },
          }),
        );
      } catch (refreshError) {
        // If refresh fails, logout user (optional: handle globally)
        localStorage.removeItem("accessToken");
        throw new Error("Session expired. Please login again.");
      }
    }

    // Handle 404 errors specifically
    // if (response.status === 404) {
    //   if (this.navigationHandler) {
    //     this.navigationHandler("/404");
    //   }
    //   throw await response.json();
    // }
    if (!response.ok) {
      track("api_error", {
        endpoint: normalizeEndpoint(endpoint),
        method: defaultOptions.method,
        status: response.status,
      });
      throw await response.json();
    }

    return await response.json();
  }

  // ==================== USER ENDPOINTS ====================

  async updateUserProfile(profileData) {
    return this.makeRequest("/users", {
      method: "PUT",
      body: JSON.stringify(profileData),
    });
  }

  async uploadProfileImage(imageFile) {
    const formData = new FormData();
    formData.append("profileImage", imageFile);

    return this.makeRequest("/users/profile-image", {
      method: "POST",
      body: formData,
      // Don't set Content-Type - let browser set it with boundary
      skipContentType: true,
    });
  }

  async sendVerificationEmail(token) {
    return this.makeRequest("/users/send-verification-email", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async fetchListPaymentMethods() {
    return this.makeRequest("/payments/payment-methods");
  }

  async deletePaymentMethod(paymentMethodId) {
    return this.makeRequest(`/payments/payment-methods/${paymentMethodId}`, {
      method: "DELETE",
    });
  }

  async checkMigratedUser(email) {
    return this.makeRequest(`/users/check-migrated`, {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  }

  async markUserNotMigrated(token) {
    return this.makeRequest(`/users/mark-not-migrated`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async forgotPassword(email) {
    return this.makeRequest(`/users/forgot-password`, {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  }

  async fetchUserTransactions(filters = {}) {
    const params = new URLSearchParams(filters);
    return this.makeRequest(`/payments?${params}`);
  }

  // ==================== GAME ENDPOINTS ====================

  async fetchGames(filters = {}) {
    const params = new URLSearchParams(filters);
    return this.makeRequest(`/games?${params}`);
  }

  async fetchUserGames(filters = {}) {
    const params = new URLSearchParams(filters);
    return this.makeRequest(`/games/user?${params}`);
  }

  async createGame(gameData) {
    const result = await this.makeRequest("/games", {
      method: "POST",
      body: JSON.stringify(gameData),
    });
    track("game_created", { payment_mode: gameData?.payment_mode });
    return result;
  }

  async fetchGameById(gameId) {
    return this.makeRequest(`/games/${gameId}`);
  }

  async updateGame(gameId, gameData) {
    return this.makeRequest(`/games/${gameId}`, {
      method: "PUT",
      body: JSON.stringify(gameData),
    });
  }

  async deleteGame(gameId) {
    return this.makeRequest(`/games/${gameId}`, {
      method: "DELETE",
    });
  }

  async cancelGame(gameId) {
    return this.makeRequest(`/games/${gameId}/cancel`, {
      method: "PATCH",
    });
  }

  async fetchGamePlayers(gameId) {
    return this.makeRequest(`/games/${gameId}/players`, {
      headers: {
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
        Expires: "0",
      },
    });
  }

  async joinGame(gameId, payload = {}) {
    const result = await this.makeRequest(`/games/${gameId}/join`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
    track("game_join_completed", {
      extra_players: payload?.unregistered_players?.length || 0,
    });
    return result;
  }

  async leaveGame(gameId) {
    const result = await this.makeRequest(`/games/${gameId}/leave`, {
      method: "POST",
    });
    track("game_left");
    return result;
  }

  async removePlayerFromGame(gameId, playerId) {
    return this.makeRequest(`/games/${gameId}/players/${playerId}`, {
      method: "DELETE",
    });
  }

  async joinWaitlist(gameId) {
    return this.makeRequest(`/games/${gameId}/waitlist`, {
      method: "POST",
    });
  }

  async leaveWaitlist(gameId) {
    return this.makeRequest(`/games/${gameId}/waitlist`, {
      method: "DELETE",
    });
  }

  async fetchGameWaitlist(gameId) {
    return this.makeRequest(`/games/${gameId}/waitlist`);
  }

  async generateTeams(gameId) {
    return this.makeRequest(`/games/${gameId}/assign-teams`, {
      method: "POST",
    });
  }

  async saveTeamAssignments(gameId, teamData) {
    return this.makeRequest(`/games/${gameId}/save-team-assignments`, {
      method: "POST",
      body: JSON.stringify(teamData),
    });
  }

  async deleteTeams(gameId) {
    return this.makeRequest(`/games/${gameId}/teams`, {
      method: "DELETE",
    });
  }

  async updateUnregisteredPlayer(gameId, playerId, playerData) {
    return this.makeRequest(`/games/${gameId}/players/${playerId}`, {
      method: "PUT",
      body: JSON.stringify(playerData),
    });
  }

  async removeUnregisteredPlayer(gameId, playerId) {
    return this.makeRequest(
      `/games/${gameId}/players/${playerId}/unregistered`,
      {
        method: "DELETE",
      },
    );
  }

  async changePlayerPaidStatus(gameId, playerId, payload) {
    return this.makeRequest(
      `/games/${gameId}/players/${playerId}/payment-status`,
      {
        method: "PATCH",
        body: JSON.stringify(payload),
      },
    );
  }

  async invitePlayersToGame(gameId, payload) {
    return this.makeRequest(`/games/${gameId}/invite`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  async respondToGameInvite(gameId, payload) {
    return this.makeRequest(`/games/${gameId}/invite/respond`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  }

  // ==================== GROUP ENDPOINTS ====================

  async fetchGroups(filters = {}) {
    const params = new URLSearchParams(filters);
    return this.makeRequest(`/groups?${params}`);
  }

  async fetchUserGroups(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.makeRequest(`/groups/user?${queryString}`);
  }

  async createGroup(groupData) {
    return this.makeRequest("/groups", {
      method: "POST",
      body: JSON.stringify(groupData),
    });
  }

  async createGroupWithAvatar(groupData) {
    return this.makeRequest("/groups/with-avatar", {
      method: "POST",
      body: groupData, // Expecting FormData
      skipContentType: true, // Let browser set Content-Type with boundary
    });
  }

  async fetchGroupById(groupId) {
    return this.makeRequest(`/groups/${groupId}`);
  }

  async updateGroup(groupId, groupData) {
    return this.makeRequest(`/groups/${groupId}`, {
      method: "PUT",
      body: JSON.stringify(groupData),
    });
  }

  async deleteGroup(groupId) {
    return this.makeRequest(`/groups/${groupId}`, {
      method: "DELETE",
    });
  }

  async uploadGroupImage(groupId, imageFile) {
    const formData = new FormData();
    formData.append("profileImage", imageFile);

    return this.makeRequest(`/groups/${groupId}/avatar`, {
      method: "POST",
      body: formData,
      // Don't set Content-Type - let browser set it with boundary
      skipContentType: true,
    });
  }

  async fetchGroupMembers(groupId, filters = {}) {
    const params = new URLSearchParams(filters);
    return this.makeRequest(`/groups/${groupId}/members?${params}`);
  }

  // ==================== PAYMENT ENDPOINTS ====================
  async connectStripeAccount(payload) {
    return this.makeRequest("/stripe/connect", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  async createPaymentIntent(payload) {
    const result = await this.makeRequest("/payments/create-intent", {
      method: "POST",
      body: JSON.stringify(payload),
    });
    // NOTE: this marks intent creation, not a confirmed charge. True
    // payment success/failure is only known after Stripe confirms the card
    // in the checkout component — add a `payment_succeeded`/`payment_failed`
    // event at that Stripe-confirmation call site for accurate revenue data.
    track("payment_intent_created");
    return result;
  }

  async joinWithSavedCard(payload = {}) {
    const result = await this.makeRequest(`/payments/pay-with-saved-method`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
    // Server-side charge of a saved method — a completed paid join.
    track("payment_succeeded", { method: "saved_card" });
    track("game_join_completed", { paid: true, method: "saved_card" });
    return result;
  }

  async fetchPayoutSummary() {
    return this.makeRequest("/stripe/payout-summary");
  }
}

// Create and export a singleton instance
const apiService = new ApiService();
export default apiService;
